import {
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { useConversationScroll } from '../page-sidebar/useConversationScroll';

import { AiAssistantComposer } from './AiAssistantComposer';
import { AiAssistantHeader } from './AiAssistantHeader';
import { AiAssistantMessageList } from './AiAssistantMessageList';
import { ChatList } from './ChatList';
import { stripCitationTokens } from './chatSources';
import { Fab } from './Fab';
import { sourceByCitationNumber } from './groupAssistantSources';
import { openAssistantSource } from './openAssistantSource';
import { SourcesPanel } from './SourcesPanel';
import {
  type AiAssistantChat,
  type AiAssistantDocumentContext,
  type AiAssistantMessage as AiAssistantMessageModel,
  type AiAssistantPageContext,
  type AiAssistantPreviewDocument,
  type AiAssistantSource,
} from './types';
import { useActiveSources } from './useActiveSources';
import { useAssistantLayoutWidths } from './useAssistantLayoutWidths';
import { useAssistantPanelWidth } from './useAssistantPanelWidth';

import styles from './AiAssistant.module.scss';

async function copyAssistantMessage(
  message: AiAssistantMessageModel
): Promise<boolean> {
  const clipboard = navigator.clipboard;
  if (!clipboard?.writeText) {
    return false;
  }

  try {
    await clipboard.writeText(stripCitationTokens(message.text));
    return true;
  } catch {
    // Clipboard may be unavailable (e.g. insecure context); ignore.
    return false;
  }
}

export type AiAssistantProps = {
  /** The chat currently shown; `null` renders an empty conversation. */
  activeChatId: string | null;
  chats: AiAssistantChat[];
  composerDisabled?: boolean;
  onActiveChatChange: (chatId: string) => void;
  /** Close the document preview (e.g. when a page/link source is opened). */
  onCloseDocument?: () => void;
  onCreateChat: () => void;
  onDeleteChat: (chatId: string) => void;
  /** Requests a backend-created archive containing the visible documents. */
  onDownloadAllSources?: () => void;
  onDownloadSource?: (source: AiAssistantSource) => void;
  /** Routing is owned by the consumer; page sources call back with their href. */
  onNavigate?: (to: string) => void;
  onOpenChange: (open: boolean) => void;
  onOpenDocument?: (document: AiAssistantPreviewDocument) => void;
  /** Width taken on the right by the assistant (chat + sources) while open. */
  onOpenWidthChange?: (width: number) => void;
  onPinnedChange?: (pinned: boolean) => void;
  /** Sources panel visibility so the host can close competing page sidebars. */
  onSourcesOpenChange?: (open: boolean) => void;
  /** Width the page layout must reserve while the assistant is pinned. */
  onReservedWidthChange?: (width: number) => void;
  onSendMessage: (
    message: string,
    pageContexts?: AiAssistantPageContext[],
    documentContexts?: AiAssistantDocumentContext[]
  ) => void;
  /** Documents selected in a table or opened in preview. */
  availableDocuments?: AiAssistantDocumentContext[];
  /** Snapshot of the portal page the user is currently viewing. */
  currentPage?: AiAssistantPageContext | null;
  open: boolean;
  /**
   * The file currently shown in the document preview (null when closed) so the
   * sources panel can clear its "opened" highlight when the preview closes.
   */
  openDocumentFile?: string | null;
  pinned?: boolean;
};

export function AiAssistant({
  activeChatId,
  availableDocuments,
  chats,
  composerDisabled = false,
  currentPage,
  onActiveChatChange,
  onCloseDocument,
  onCreateChat,
  onDeleteChat,
  onDownloadAllSources,
  onDownloadSource,
  onNavigate,
  onOpenChange,
  onOpenDocument,
  onOpenWidthChange,
  onPinnedChange,
  onReservedWidthChange,
  onSourcesOpenChange,
  onSendMessage,
  open,
  openDocumentFile = null,
  pinned = false,
}: AiAssistantProps) {
  const { t } = useTranslation();
  const [view, setView] = useState<'chat' | 'list'>('chat');
  const messagesRef = useRef<HTMLDivElement>(null);

  const {
    activeSources,
    closeSources,
    jumpToSource,
    openSourcesForMessage,
    sourcesTarget,
  } = useActiveSources({ activeChatId, open, view });

  const sourcesOpen = open && view === 'chat' && activeSources !== null;

  const {
    isRailOpen,
    isRailSplit,
    isResizing,
    layoutPanelWidth,
    panelRef,
    panelStyle,
    setSourcesWidth,
    sourcesWidth,
    startResizeOuter,
    startResizeSplit,
    toggleRail,
  } = useAssistantPanelWidth({
    documentOpen: openDocumentFile != null,
    isRailOpenEnabled: view === 'chat',
    sourcesOpen,
  });

  // Chip click opens the previewer. When the sources panel is already open,
  // also select and scroll to that citation so the list matches the chip.
  function openCitedSource(
    message: AiAssistantMessageModel,
    number: number
  ): void {
    const source = sourceByCitationNumber(message.sources ?? [], number);
    if (!source) {
      return;
    }
    if (activeSources) {
      jumpToSource(message, number);
    }
    openAssistantSource(source, {
      onCloseDocument,
      onNavigate,
      onOpenDocument,
    });
  }

  const activeChat = chats.find((chat) => chat.id === activeChatId) ?? null;

  const conversationScrollKey = (activeChat?.messages ?? [])
    .map(
      (message) =>
        `${message.id}:${message.text}:${(message.statuses ?? []).map((status) => status.content).join('\u001F')}:${(message.completedStatuses ?? []).map((status) => status.content).join('\u001F')}:${String(message.complete)}`
    )
    .join('\n');
  const { anchorConversation, releaseConversationAnchor, scrollToBottom } =
    useConversationScroll(
      messagesRef,
      conversationScrollKey,
      open && view === 'chat'
    );

  useEffect(() => {
    if (open && view === 'chat') {
      scrollToBottom();
    }
  }, [activeChatId, open, scrollToBottom, view]);

  // Hold the grabbed spot still for the whole drag, then let the conversation
  // resume its normal follow-the-bottom behavior.
  useEffect(() => {
    if (!isResizing) {
      releaseConversationAnchor();
    }
  }, [isResizing, releaseConversationAnchor]);

  function startResizeFromHandle(
    event: ReactPointerEvent<HTMLElement>,
    startResize: (event: ReactPointerEvent<HTMLElement>) => void
  ) {
    // startResize ignores non-primary buttons, so only store an anchor when a
    // drag will actually start. Otherwise later layout growth holds a stale spot.
    if (event.button !== 0) {
      return;
    }

    anchorConversation(event.clientY);
    startResize(event);
  }

  // Switching chats invalidates the previewer's citation/page context
  // (belongs to the previous chat's message); close it alongside the sources
  // panel, which `useActiveSources` already resets on `activeChatId` change.
  // Skip the first run so mounting with a pre-selected chat doesn't fire it.
  const previousChatIdRef = useRef(activeChatId);
  useEffect(() => {
    if (previousChatIdRef.current !== activeChatId) {
      previousChatIdRef.current = activeChatId;
      onCloseDocument?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- close on chat switch only, not on every onCloseDocument identity change
  }, [activeChatId]);

  useAssistantLayoutWidths({
    onOpenWidthChange,
    onReservedWidthChange,
    open,
    panelWidth: layoutPanelWidth,
    pinned,
    sourcesOpen,
    sourcesWidth,
  });

  useEffect(() => {
    onSourcesOpenChange?.(sourcesOpen);
  }, [onSourcesOpenChange, sourcesOpen]);

  useEffect(() => {
    return () => onSourcesOpenChange?.(false);
  }, [onSourcesOpenChange]);

  useEffect(() => {
    if (!sourcesOpen && !openDocumentFile) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape' || event.defaultPrevented) {
        return;
      }

      if (openDocumentFile) {
        onCloseDocument?.();
      }
      if (sourcesOpen) {
        closeSources();
      }
    }

    document.documentElement.addEventListener('keydown', handleKeyDown);
    return () =>
      document.documentElement.removeEventListener('keydown', handleKeyDown);
  }, [closeSources, onCloseDocument, openDocumentFile, sourcesOpen]);

  function openChat(chatId: string) {
    onActiveChatChange(chatId);
    setView('chat');
  }

  function handleCreateChat() {
    onCreateChat();
    setView('chat');
  }

  function sendMessage(
    message: string,
    pageContexts: AiAssistantPageContext[] = [],
    documentContexts: AiAssistantDocumentContext[] = []
  ) {
    if (!activeChat) {
      return false;
    }

    scrollToBottom();
    onSendMessage(message, pageContexts, documentContexts);
    return true;
  }

  const chatList = (
    <ChatList
      activeChatId={activeChatId}
      chats={chats.map((chat) => ({
        id: chat.id,
        title: chat.title,
        createdAt: chat.createdAt,
        updatedAt: chat.updatedAt,
      }))}
      onActiveChatChange={openChat}
      onCreateChat={handleCreateChat}
      onDeleteChat={onDeleteChat}
    />
  );

  return (
    <>
      <aside
        aria-hidden={!open}
        aria-label={t('app.aiAssistant.assistantTitle')}
        className={clsx(styles['panel'], {
          [styles['panel--open']]: open,
          [styles['panel--pinned']]: pinned,
          [styles['panel--rail-open']]: open && view === 'chat' && isRailOpen,
          [styles['panel--resizing']]: isResizing,
        })}
        data-testid="ai-assistant-panel"
        ref={panelRef}
        role="dialog"
        style={panelStyle}
      >
        <div
          aria-hidden="true"
          className={styles['resize-handle']}
          data-resize-target={isRailSplit ? 'rail-outer' : 'panel'}
          data-testid="ai-assistant-resize-handle"
          onPointerDown={(event) =>
            startResizeFromHandle(event, startResizeOuter)
          }
        />
        {isRailSplit ? (
          <div
            aria-hidden="true"
            className={clsx(
              styles['resize-handle'],
              styles['resize-handle--split']
            )}
            data-resize-target="rail"
            data-testid="ai-assistant-resize-handle-split"
            onPointerDown={(event) =>
              startResizeFromHandle(event, startResizeSplit)
            }
          />
        ) : null}

        <AiAssistantHeader
          activeChatTitle={activeChat?.title}
          isRailOpen={isRailOpen}
          onClose={() => onOpenChange(false)}
          onCreateChat={handleCreateChat}
          onShowChatList={() => setView('list')}
          onTogglePinned={() => onPinnedChange?.(!pinned)}
          onToggleRail={toggleRail}
          pinned={pinned}
          view={view}
        />

        {view === 'chat' ? (
          <div className={styles['panel-body']}>
            <aside
              aria-hidden={!isRailOpen}
              className={clsx(styles['chat-rail'], {
                [styles['chat-rail--open']]: isRailOpen,
              })}
            >
              {isRailOpen ? (
                <div className={styles['chat-rail-inner']}>{chatList}</div>
              ) : null}
            </aside>
            <div className={styles['chat-main']}>
              <div
                className={styles['panel-messages']}
                data-testid="ai-assistant-messages"
                ref={messagesRef}
              >
                <AiAssistantMessageList
                  activeSourcesMessageId={activeSources?.messageId}
                  messages={activeChat?.messages ?? []}
                  onCopy={copyAssistantMessage}
                  onJumpToSource={openCitedSource}
                  onNavigate={onNavigate}
                  onOpenSources={openSourcesForMessage}
                />
              </div>

              <AiAssistantComposer
                availableDocuments={availableDocuments}
                currentPage={currentPage}
                disabled={!activeChat || composerDisabled}
                onSend={sendMessage}
              />
            </div>
          </div>
        ) : (
          <div className={styles['chat-list-view']}>{chatList}</div>
        )}
      </aside>

      <SourcesPanel
        data={open && view === 'chat' ? activeSources : null}
        onClose={closeSources}
        onCloseDocument={onCloseDocument}
        onDownloadAll={onDownloadAllSources}
        onDownloadSource={onDownloadSource}
        onNavigate={onNavigate}
        onOpenDocument={onOpenDocument}
        onWidthChange={setSourcesWidth}
        openDocumentFile={openDocumentFile}
        rightOffset={open ? layoutPanelWidth : 0}
        targetSource={sourcesTarget}
        width={sourcesWidth}
      />

      <Fab hidden={open} onClick={() => onOpenChange(true)} />
    </>
  );
}
